// XLAvatar - Sprint 44 XL.
// Avatar circular (o cuadrado) con fallback a iniciales. Color de fondo
// derivado deterministicamente del nombre para consistencia visual
// (mismo usuario = mismo color). Sin libs externas. Soporta dark mode.
//
// Uso:
//   <XLAvatar name="Javier Gonzalez" size="md" />
//   <XLAvatar name="Maria" src="/media/u/5.jpg" size="lg" />
//   <XLAvatar name="AB" shape="square" />
import { useEffect, useMemo, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';

const CSS_ID = 'xl-avatar-css';
const CSS = `
.xl-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--xl-font-sans);
  font-weight: 600;
  color: var(--xl-color-text-inverse);
  background: var(--xl-color-text-secondary);
  overflow: hidden;
  flex-shrink: 0;
  user-select: none;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.xl-avatar--circle { border-radius: var(--xl-radius-full) }
.xl-avatar--square { border-radius: var(--xl-radius-md) }

.xl-avatar--sm { width: 24px;  height: 24px;  font-size: 10px }
.xl-avatar--md { width: 36px;  height: 36px;  font-size: 13px }
.xl-avatar--lg { width: 48px;  height: 48px;  font-size: 16px }
.xl-avatar--xl { width: 64px;  height: 64px;  font-size: 20px }

.xl-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.xl-avatar__ring {
  box-shadow: 0 0 0 2px var(--xl-color-bg), 0 0 0 3px var(--xl-color-primary);
}
[data-xl-theme="dark"] .xl-avatar__ring {
  box-shadow: 0 0 0 2px var(--xl-color-bg), 0 0 0 3px var(--xl-color-primary);
}
`;

// Paleta deterministica (hash suave del nombre -> index en paleta).
// Colores con buen contraste AA contra blanco en fondo solido.
const PALETTE = [
  '#2563EB', '#0EA5E9', '#059669', '#10B981', '#D97706',
  '#DC2626', '#9333EA', '#DB2777', '#475569', '#0891B2',
];

function hashCode(str) {
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0; // int32
  }
  return Math.abs(h);
}

function initialsOf(name) {
  const s = String(name || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function XLAvatar({
  name,
  src,
  size = 'md',
  shape = 'circle',
  ring = false,
  className = '',
  title,
  ariaLabel,
}) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);

  const color = useMemo(() => {
    const idx = hashCode(name || '') % PALETTE.length;
    return PALETTE[idx];
  }, [name]);

  const cls = [
    'xl-avatar',
    'xl-avatar--' + shape,
    'xl-avatar--' + size,
    ring ? 'xl-avatar__ring' : '',
    className,
  ].filter(Boolean).join(' ');

  const showImg = !!src && !broken;
  const style = showImg ? undefined : { background: color };
  const ariaTitle = title || name || ariaLabel;

  return (
    <span
      className={cls}
      style={style}
      title={ariaTitle}
      aria-label={ariaLabel || (name ? ('Usuario ' + name) : 'Avatar')}
      role="img"
    >
      {showImg ? (
        <img
          className="xl-avatar__img"
          src={src}
          alt=""
          onError={() => setBroken(true)}
        />
      ) : initialsOf(name)}
    </span>
  );
}
